import type { BoxProps } from '@hover/blueprint';
import { Body, Box, Button } from '@hover/blueprint';
import { Flex } from '@hover/blueprint/chakra';
import React from 'react';

import { MenuHeader } from 'src/modules/designStudio/components/DesignMenu/Menu/MenuHeader';
import { ProductIcon } from 'src/modules/designStudio/components/DesignMenu/Menu/TemplateMenu/products/ProductIcon';
import { getMaterialColor } from 'src/modules/designStudio/components/DesignMenu/Menu/TemplateMenu/products/utils';
import { ProductTypeNames } from 'src/modules/designStudio/helpers/productSelection/constants';
import type { MaterialGroup } from 'src/modules/designStudio/helpers/productSelection/types';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

interface TemplateProductDetailsProps extends BoxProps {
  closeMenu: () => void;
  onBack: () => void;
  onOrderSample: () => void;
  material: MaterialGroup;
}

export const TemplateProductDetails = ({
  material,
  closeMenu,
  onOrderSample,
  onBack,
  ...props
}: TemplateProductDetailsProps) => {
  const variation = material.variation!;
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  const colorName = getMaterialColor(material);
  const details = [
    {
      title: t('menu.template.products.type'),
      value: ProductTypeNames[variation.product.type],
    },
    { title: t('menu.template.products.size'), value: variation.facets.sizing },
    {
      title: t('menu.template.products.color'),
      value: colorName,
    },
  ];

  return (
    <Box
      alignItems="stretch"
      bottom="0"
      flexDirection="column"
      height="100%"
      left="0"
      overflowX="hidden"
      padding="600"
      position="absolute"
      right="0"
      top="0"
      {...props}
    >
      <MenuHeader
        closeMenu={closeMenu}
        headerText={t('menu.template.productTitle')!}
        onBack={onBack}
      />
      <Flex direction="column">
        <Body fontSize={300} fontWeight={700} lineHeight={300}>
          {variation.product.name}
        </Body>
        <Flex direction="row" justifyItems="center">
          <ProductIcon
            color={material.appearance.color ?? '#ffffff'}
            flexBasis={0}
            flexGrow={1}
            isSelected
            thumbnail={variation.thumbnailURL ?? ''}
          />

          <Flex
            alignItems="center"
            direction="column"
            flexBasis={0}
            flexGrow={1}
            gap={400}
          >
            {details
              .filter(e => e.value)
              .map(entry => (
                <Flex
                  direction="column"
                  gap={100}
                  key={entry.title}
                  width="50%"
                >
                  <Body
                    color="neutral.600"
                    fontSize={200}
                    fontWeight={600}
                    lineHeight={200}
                    m={0}
                    width="fit-content"
                  >
                    {entry.title}
                  </Body>
                  <Body
                    fontSize={200}
                    fontWeight={400}
                    lineHeight={200}
                    m={0}
                    width="fit-content"
                  >
                    {entry.value}
                  </Body>
                </Flex>
              ))}
          </Flex>
        </Flex>
        {material.variation!.manufacturerURL && (
          <Button
            my={400}
            onClick={onOrderSample}
            py={500}
            shape="box"
            variant="secondary"
          >
            {t('menu.template.products.orderSample')}
          </Button>
        )}
      </Flex>
    </Box>
  );
};
